import { CanvasRenderer, rgbCss, type StringStyle, type ViewTransform } from './CanvasRenderer';
import type { Rgb } from '@/types/engine';

export interface OverlayOptions {
  showPins: boolean;
  showPinIds: boolean;
  showCandidates: boolean;
  pinColor: Rgb;
  pinRadius: number;
  selected: number | null;
  hovered: number | null;
  currentPin: number | null;
  pendingPin: number | null;
  locked: ReadonlySet<number>;
}

export type HitResult = { kind: 'string'; index: number; distance: number } | { kind: 'pin'; index: number; distance: number } | null;

export interface RenderFrame {
  upTo: number;
  underlay: ImageData | null;
  underlayOpacity: number;
  overlay: OverlayOptions;
}

export class StringArtRenderer {
  readonly container: HTMLElement;
  /** Sits under the canvases and casts the artboard's shadow onto the desk. */
  private readonly shadow: HTMLDivElement;
  private readonly underlayCanvas: HTMLCanvasElement;
  private readonly stringsCanvas: HTMLCanvasElement;
  private readonly overlayCanvas: HTMLCanvasElement;
  private readonly strings: CanvasRenderer;
  private readonly underlayCtx: CanvasRenderingContext2D;
  private readonly overlayCtx: CanvasRenderingContext2D;
  private width = 1;
  private height = 1;
  private dpr = 1;
  private targetWidth = 1;
  private targetHeight = 1;
  private transform: ViewTransform = { scale: 1, offsetX: 0, offsetY: 0 };
  private userTransform: ViewTransform | null = null;
  private pins: Float32Array = new Float32Array(0);
  private sequence: Uint16Array = new Uint16Array(0);
  private underlayImage: ImageData | null = null;
  private underlayBitmap: ImageBitmap | HTMLCanvasElement | null = null;
  private underlayDirty = true;
  private lastFrame: RenderFrame | null = null;
  private raf: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private lastRenderMs = 0;

  constructor(container: HTMLElement) {
    this.container = container;
    container.classList.add('sa-stage');
    this.shadow = document.createElement('div');
    this.shadow.className = 'sa-shadow';
    container.appendChild(this.shadow);
    this.underlayCanvas = document.createElement('canvas');
    this.stringsCanvas = document.createElement('canvas');
    this.overlayCanvas = document.createElement('canvas');
    for (const [c, name] of [
      [this.underlayCanvas, 'underlay'],
      [this.stringsCanvas, 'strings'],
      [this.overlayCanvas, 'overlay'],
    ] as const) {
      c.className = `sa-layer sa-layer-${name}`;
      container.appendChild(c);
    }
    this.strings = new CanvasRenderer(this.stringsCanvas);
    this.underlayCtx = this.underlayCanvas.getContext('2d')!;
    this.overlayCtx = this.overlayCanvas.getContext('2d')!;
    if (typeof ResizeObserver !== 'undefined') {
      // Defer to the next frame so layout changes never re-trigger the observer synchronously.
      this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => this.resize()));
      this.resizeObserver.observe(container);
    }
    this.resize();
  }

  get renderMs(): number {
    return this.lastRenderMs;
  }

  get viewTransform(): ViewTransform {
    return { ...this.transform };
  }

  resize(): void {
    const rect = this.container.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    if (width === this.width && height === this.height && dpr === this.dpr) return;
    this.width = width;
    this.height = height;
    this.dpr = dpr;
    for (const c of [this.underlayCanvas, this.overlayCanvas]) {
      c.width = Math.round(width * dpr);
      c.height = Math.round(height * dpr);
      c.style.width = `${width}px`;
      c.style.height = `${height}px`;
    }
    this.stringsCanvas.style.width = `${width}px`;
    this.stringsCanvas.style.height = `${height}px`;
    this.strings.setViewport(width, height, dpr);
    this.updateTransform();
    this.underlayDirty = true;
    this.requestRender();
  }

  setTargetSize(width: number, height: number): void {
    if (width === this.targetWidth && height === this.targetHeight) return;
    this.targetWidth = Math.max(1, width);
    this.targetHeight = Math.max(1, height);
    this.strings.setTargetSize(this.targetWidth, this.targetHeight);
    this.userTransform = null;
    this.updateTransform();
    this.underlayDirty = true;
    this.requestRender();
  }

  setSolution(pins: Float32Array, sequence: Uint16Array): void {
    this.pins = pins;
    this.sequence = sequence;
    this.strings.setPins(pins);
    this.strings.setSequence(sequence);
    this.requestRender();
  }

  setStyle(style: StringStyle): void {
    this.strings.setStyle(style);
    this.requestRender();
  }

  setUnderlay(image: ImageData | null): void {
    if (image === this.underlayImage) return;
    this.underlayImage = image;
    this.underlayDirty = true;
    this.requestRender();
  }

  resetView(): void {
    this.userTransform = null;
    this.updateTransform();
    this.requestRender();
  }

  zoomAt(factor: number, cssX: number, cssY: number): void {
    const t = this.transform;
    const scale = Math.max(t.scale * 0.1, Math.min(t.scale * 40, t.scale * factor));
    const k = scale / t.scale;
    this.userTransform = {
      scale,
      offsetX: cssX - (cssX - t.offsetX) * k,
      offsetY: cssY - (cssY - t.offsetY) * k,
    };
    this.updateTransform();
    this.requestRender();
  }

  panBy(dx: number, dy: number): void {
    const t = this.transform;
    this.userTransform = { scale: t.scale, offsetX: t.offsetX + dx, offsetY: t.offsetY + dy };
    this.updateTransform();
    this.requestRender();
  }

  private updateTransform(): void {
    if (this.userTransform) {
      this.transform = { ...this.userTransform };
    } else {
      const margin = 40; // desk showing around the artboard, like a sheet on a table
      const scale = Math.min((this.width - margin * 2) / this.targetWidth, (this.height - margin * 2) / this.targetHeight);
      const s = Math.max(1e-3, scale);
      this.transform = {
        scale: s,
        offsetX: (this.width - this.targetWidth * s) / 2,
        offsetY: (this.height - this.targetHeight * s) / 2,
      };
    }
    this.strings.setTransform(this.transform);
    this.underlayDirty = true;
    this.placeShadow();
  }

  private placeShadow(): void {
    const { scale, offsetX, offsetY } = this.transform;
    const st = this.shadow.style;
    st.left = `${offsetX}px`;
    st.top = `${offsetY}px`;
    st.width = `${this.targetWidth * scale}px`;
    st.height = `${this.targetHeight * scale}px`;
  }

  toTarget(cssX: number, cssY: number): [number, number] {
    const { scale, offsetX, offsetY } = this.transform;
    return [(cssX - offsetX) / scale, (cssY - offsetY) / scale];
  }

  toCss(x: number, y: number): [number, number] {
    const { scale, offsetX, offsetY } = this.transform;
    return [x * scale + offsetX, y * scale + offsetY];
  }

  hitTest(cssX: number, cssY: number, upTo: number, toleranceCss = 6): HitResult {
    const [tx, ty] = this.toTarget(cssX, cssY);
    const tol = toleranceCss / this.transform.scale;
    const pins = this.pins;
    const pinCount = pins.length >>> 1;
    let best: HitResult = null;
    for (let i = 0; i < pinCount; i++) {
      const d = Math.hypot(pins[i * 2] - tx, pins[i * 2 + 1] - ty);
      if (d <= tol * 1.5 && (!best || d < best.distance)) best = { kind: 'pin', index: i, distance: d };
    }
    if (best) return best;
    const n = Math.min(upTo, this.sequence.length >>> 1);
    for (let i = 0; i < n; i++) {
      const a = this.sequence[i * 2];
      const b = this.sequence[i * 2 + 1];
      if (a >= pinCount || b >= pinCount) continue;
      const d = pointSegmentDistance(tx, ty, pins[a * 2], pins[a * 2 + 1], pins[b * 2], pins[b * 2 + 1]);
      if (d <= tol && (!best || d < best.distance)) best = { kind: 'string', index: i, distance: d };
    }
    return best;
  }

  render(frame: RenderFrame): void {
    this.lastFrame = frame;
    this.requestRender();
  }

  private requestRender(): void {
    if (this.raf !== null || !this.lastFrame) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this.paint();
    });
  }

  paint(): void {
    const frame = this.lastFrame;
    if (!frame) return;
    const t0 = performance.now();
    this.paintUnderlay(frame);
    this.strings.draw(frame.upTo);
    this.paintOverlay(frame);
    this.lastRenderMs = performance.now() - t0;
  }

  private paintUnderlay(frame: RenderFrame): void {
    const ctx = this.underlayCtx;
    if (frame.underlay !== this.underlayImage) {
      this.underlayImage = frame.underlay;
      this.underlayDirty = true;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.underlayCanvas.width, this.underlayCanvas.height);
    if (!this.underlayImage) {
      this.underlayCanvas.style.opacity = '0';
      return;
    }
    if (this.underlayDirty || !this.underlayBitmap) {
      const c = document.createElement('canvas');
      c.width = this.underlayImage.width;
      c.height = this.underlayImage.height;
      c.getContext('2d')!.putImageData(this.underlayImage, 0, 0);
      this.underlayBitmap = c;
      this.underlayDirty = false;
    }
    const { scale, offsetX, offsetY } = this.transform;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.imageSmoothingEnabled = scale < 1;
    ctx.drawImage(this.underlayBitmap, offsetX, offsetY, this.targetWidth * scale, this.targetHeight * scale);
    this.underlayCanvas.style.opacity = String(frame.underlayOpacity);
    this.stringsCanvas.style.opacity = frame.underlay ? String(1 - frame.underlayOpacity * 0.85) : '1';
  }

  private paintOverlay(frame: RenderFrame): void {
    const ctx = this.overlayCtx;
    const o = frame.overlay;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const pins = this.pins;
    const pinCount = pins.length >>> 1;
    const { scale } = this.transform;

    const drawString = (index: number, color: string, width: number) => {
      if (index < 0 || index >= (this.sequence.length >>> 1)) return;
      const a = this.sequence[index * 2];
      const b = this.sequence[index * 2 + 1];
      if (a >= pinCount || b >= pinCount) return;
      const [x1, y1] = this.toCss(pins[a * 2], pins[a * 2 + 1]);
      const [x2, y2] = this.toCss(pins[b * 2], pins[b * 2 + 1]);
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    };

    if (o.showCandidates && o.currentPin !== null && o.currentPin < pinCount) {
      const [cx, cy] = this.toCss(pins[o.currentPin * 2], pins[o.currentPin * 2 + 1]);
      ctx.strokeStyle = 'rgba(192, 68, 46, 0.14)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i < pinCount; i++) {
        if (i === o.currentPin) continue;
        const [x, y] = this.toCss(pins[i * 2], pins[i * 2 + 1]);
        ctx.moveTo(cx, cy);
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    for (const i of o.locked) {
      if (i < frame.upTo) drawString(i, 'rgba(63, 138, 90, 0.6)', 2.5);
    }
    if (o.hovered !== null && o.hovered !== o.selected) drawString(o.hovered, 'rgba(28, 26, 23, 0.45)', 3);
    if (o.selected !== null) drawString(o.selected, 'rgba(192, 68, 46, 0.95)', 3.5);

    if (o.showPins && pinCount > 0) {
      const r = Math.max(1.5, Math.min(6, o.pinRadius * Math.max(0.5, Math.min(2, scale))));
      ctx.fillStyle = rgbCss(o.pinColor);
      ctx.strokeStyle = 'rgba(28, 26, 23, 0.35)';
      ctx.lineWidth = 0.75;
      for (let i = 0; i < pinCount; i++) {
        const [x, y] = this.toCss(pins[i * 2], pins[i * 2 + 1]);
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      if (o.showPinIds) {
        const step = Math.max(1, Math.ceil(pinCount / Math.max(8, Math.floor((this.width + this.height) / 18))));
        ctx.fillStyle = 'rgba(28, 26, 23, 0.85)';
        ctx.font = '10px "IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const cx = this.targetWidth / 2;
        const cy = this.targetHeight / 2;
        for (let i = 0; i < pinCount; i += step) {
          const px = pins[i * 2];
          const py = pins[i * 2 + 1];
          const dx = px - cx;
          const dy = py - cy;
          const len = Math.hypot(dx, dy) || 1;
          const [x, y] = this.toCss(px + (dx / len) * (14 / scale), py + (dy / len) * (14 / scale));
          ctx.fillText(String(i), x, y);
        }
      }
    }
    const highlightPin = (index: number | null, color: string, radius: number) => {
      if (index === null || index >= pinCount) return;
      const [x, y] = this.toCss(pins[index * 2], pins[index * 2 + 1]);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.stroke();
    };
    highlightPin(o.currentPin, 'rgba(63, 138, 90, 0.95)', 7);
    highlightPin(o.pendingPin, 'rgba(192, 68, 46, 0.95)', 8);
  }

  async exportPng(size: number, style: StringStyle, upTo: number, showPins: boolean, pinColor: Rgb, pinRadius: number): Promise<Blob> {
    const aspect = this.targetHeight / this.targetWidth;
    const width = aspect <= 1 ? size : Math.round(size / aspect);
    const height = aspect <= 1 ? Math.round(size * aspect) : size;
    const canvas = document.createElement('canvas');
    const renderer = new CanvasRenderer(canvas);
    renderer.setViewport(width, height, 1);
    renderer.setTargetSize(this.targetWidth, this.targetHeight);
    const scale = width / this.targetWidth;
    renderer.setTransform({ scale, offsetX: 0, offsetY: 0 });
    renderer.setPins(this.pins);
    renderer.setSequence(this.sequence);
    renderer.setStyle({ ...style, thickness: style.thickness * scale });
    renderer.draw(upTo);
    if (showPins) {
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = rgbCss(pinColor);
      const pinCount = this.pins.length >>> 1;
      for (let i = 0; i < pinCount; i++) {
        ctx.beginPath();
        ctx.arc(this.pins[i * 2] * scale, this.pins[i * 2 + 1] * scale, Math.max(1, pinRadius * scale), 0, Math.PI * 2);
        ctx.fill();
      }
    }
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG encoding failed'))), 'image/png');
    });
  }

  dispose(): void {
    this.resizeObserver?.disconnect();
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.container.replaceChildren();
  }
}

export function pointSegmentDistance(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}
